import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import {
 Heading,
 Flex,
 Box,
 useToast,
 Button,
 Text,
 Progress,
 Center,
} from '@chakra-ui/react';
import baseService from '../../../services/base';
import { downloadFile } from '../../../helpers/downloadFile';
import { DownloadResponsesButton, SubmitAnswersButton } from '../../../components/Button';
import { Select } from '../../../components/Input';
import { fetchData } from '../../../services/fetch';
import QuestionCardSCertificationType from '../../../components/Card/Question-card-certification-quest';
import { useSSO } from '../../../context/SSO';
import { AttachEvidenceModal } from '../../../components/Modals'
import StatusTag from '../../../components/Tags/StatusTag';
import CertificationStatusPanel from '../../../components/Certification/CertificationStatusPanel';
import EditStatusModal from '../../../components/Modals/EditStatus';
import RespondentViewPillar from '../../../components/Survey/RespondentViewPillar';
import { initializeDefaultValues } from './defaultValues';

async function fetchQuestionnaire(url) {
 const response = await baseService.get(url);
 if (!response?.data || Object.keys(response?.data)?.length === 0) {
  throw new Error('Empty response data');
 }
 return response.data;
}

export default function PillarCertification({
 selectedCertificationId,
 selectedCompanyId,
 companyGroupId,
 surveyId: unused,
 isResponsibleUser = false,
 companyName
}) {
 const toast = useToast();
 const { user } = useSSO();
 const [isEvidenceModalOpen, setEvidenceModalOpen] = useState(false);
 const [currentQuestionId, setCurrentQuestionId] = useState();
 const [currentFiles, setCurrentFiles] = useState([]);
 const [isLoadingEvidence, setLoadingEvidence] = useState(false);
 const [isLoadingResponses, setLoadingResponses] = useState(false);
 const [selectedPillar, setSelectedPillar] = useState();
 const [isViewMode, setIsViewMode] = useState(false);
 const [certificationStatus, setCertificationStatus] = useState(null);
 const [isEditStatusModalOpen, setIsEditStatusModalOpen] = useState(false);
 const [surveyId, setSurveyId] = useState(null);

 const { data, isLoading } = useSWR(
  selectedCertificationId
   ? `certificate/questionary/${selectedCertificationId}/pillars?id_company=${selectedCompanyId}`
   : null,
  fetchQuestionnaire
 );

 const { data: currentPillarData } = useSWR(
  selectedPillar
   ? `certificate/admin/${selectedCertificationId}?id_company=${selectedCompanyId}&id_pillar=${selectedPillar}`
   : null,
  fetchData
 );

 useEffect(() => {
  if (data && data.length > 0) {
    const idSurvey = data[0].id_survey;
    setSurveyId(idSurvey);
  }
 }, [isLoading, data, unused])

 const {
  data: statusData,
  isLoading: isLoadingStatus,
  mutate,
 } = useSWR('certificate-status-control', fetchData);

 useMemo(() => {
  if (statusData && selectedCertificationId && selectedCompanyId) {
   const matchingStatus = statusData.find(
    (status) =>
     status.certificate.id === selectedCertificationId &&
     status.company.id_company === parseInt(selectedCompanyId)
   );
   if (matchingStatus) {
    setCertificationStatus(matchingStatus);
   }
  }
 }, [statusData, selectedCertificationId, selectedCompanyId]);

 const certificationData = useMemo(() => data?.[0], [data]);
 const pillars = useMemo(() => certificationData?.themes || [], [certificationData]);
 const formName = useMemo(() => certificationData?.description, [certificationData]);

 const currentQuestions = useMemo(() => {
  if (!data) return [];
  if (currentPillarData?.pillar?.questions?.length) {
   return currentPillarData?.pillar?.questions || [];
  }
  return data?.survey?.questions || [];
 }, [data, currentPillarData]);

 const totalAnswered = useMemo(() => {
  return currentQuestions.filter((question) => {
   const response =
    question.user_responses ||
    question.users_responses?.find(
     (response) => response?.company?.id_company === +selectedCompanyId
    );
   return (
    response?.response || (response?.response_file && response.response_file.length > 0)
   );
  }).length;
 }, [currentQuestions, selectedCompanyId]);

 const pillarOptions = useMemo(() => {
  return pillars.map((pillar) => ({
   label: pillar.name_pillar,
   value: pillar.id_pillar,
  }));
 }, [pillars]);

 const totalQuestions = useMemo(() => {
  return currentQuestions.length;
 }, [currentQuestions]);

 const answeredQuestions = useMemo(() => {
  return currentQuestions.filter(
   (question) =>
    question.user_responses ||
    question.users_responses?.some(
     (response) => response?.company?.id_company === +selectedCompanyId
    )
  ).length;
 }, [currentQuestions, selectedCompanyId]);
 
 const selectedPillarIndex = useMemo(() => {
  if (!selectedPillar || !data?.pillars) return 0;

  return data.pillars.findIndex((pillar) => pillar.id_pillar === selectedPillar) + 1;
 }, [selectedPillar, data?.pillars]);

 useEffect(() => {
  if (pillarOptions.length > 0 && !selectedPillar) {
   setSelectedPillar(pillarOptions[0].value);
  }
 }, [pillarOptions, selectedPillar]);

 const hasEvidenceFiles = useMemo(() => {
  if (!currentQuestions?.length) return false;

  return currentQuestions.some((question) => {
   const responses = question?.users_responses || [question?.user_responses];

   return responses?.some(
    (response) => response?.response_file && response?.response_file.length > 0
   );
  });
 }, [currentQuestions]);

 const handleQuestionEvidence = useCallback((questionId, response) => {
  setCurrentQuestionId(questionId);
  setCurrentFiles(response?.response_file);
  setEvidenceModalOpen(true);
 }, []);

 const handleCloseCurrentEvidence = () => {
  setEvidenceModalOpen(false);
  setCurrentQuestionId(null);
  setCurrentFiles([]);
 };

 async function evidenceDownload() {
  setLoadingEvidence(true);
  try {
   const response = await baseService.get(
    `/downloads?survey=${certificationData?.id_survey}&group=${companyGroupId}&form=${formName}`,
    { responseType: 'arraybuffer' }
   );

   if (response.headers['content-type'].includes('application/json')) {
    const data = JSON.parse(new TextDecoder().decode(response.data));
    downloadFile(data.url, `${formName}_evidence.zip`);
   } else {
    const blob = new Blob([response.data], { type: 'application/zip' });
    downloadFile(window.URL.createObjectURL(blob), `${formName}_evidence.zip`);
   }
   toast({
    title: 'Download das evidências iniciado com sucesso',
    status: 'success',
   });
  } catch (error) {
   toast({
    title: 'Erro ao fazer o download das evidências',
    description: 'Não foi possível processar os arquivos anexados. Tente novamente.',
    status: 'error',
   });
  } finally {
   setLoadingEvidence(false);
  }
 }

 const defaultValues = useMemo(() => {
  return currentQuestions.length ? initializeDefaultValues(currentQuestions) : {};
 }, [currentQuestions]);

 if (!data || !pillars?.length) {
  return null;
 }

 if (isLoading || isLoadingStatus) {
  return null;
 }

 if (isViewMode) {
  return (
   <RespondentViewPillar
    defaultValues={defaultValues}
    surveyId={surveyId}
    companyId={selectedCompanyId}
    userId={user?.user?.id_user}
    questions={currentQuestions}
    companyName={currentQuestions[0]?.company?.name || companyName}
    totalAnsweredQuestions={answeredQuestions}
    onClose={() => setIsViewMode(false)}
    currentPillarData={currentPillarData}
    pillarIndex={selectedPillarIndex + 1}
    // certificationStatus={}
   />
  );
 }

 return (
  <Box>
   {certificationStatus?.id && (
    <CertificationStatusPanel control={certificationStatus} />
   )}

   <Box bg='white' p='16px' borderRadius='12px'>
    <Flex h='63px' px='16px' bg='#F9F9F9' align='center' gap='12px' mb='16px'>
     <Flex gap='12px' h='31px' align='center' ml='auto'>
      <Text fontWeight={700}>Status:</Text>
      <StatusTag status={certificationStatus?.status || 'não enviado'} />
     </Flex>

     {!isResponsibleUser &&
      certificationStatus?.status &&
      certificationStatus?.status !== 'não enviado' && (
       <Button
        variant='underline'
        fontSize={'15px'}
        fontWeight={600}
        onClick={() => setIsEditStatusModalOpen(true)}
       >
        Editar status
       </Button>
      )}
    </Flex>

    <Flex h='51px' align='center' justify='space-between' gap='6'>
     <Heading as='h3' fontSize='md'>
      Pilares
     </Heading>

     {selectedPillar && (
      <Flex flex='3' align='center' justifyContent='flex-end' gap='20px'>
       {!isResponsibleUser && (
        <Button
         variant='underline'
         fontWeight={600}
         ml='auto'
         onClick={() => setIsViewMode(true)}
        >
         Visualizar como respondente
        </Button>
       )}
       <Flex w='261px' maxW='271px'>
        <Select
         name='currentPillar'
         options={pillarOptions}
         value={selectedPillar}
         onChange={(e) => setSelectedPillar(+e.target.value)}
         h='49px'
        />
       </Flex>

       <Flex w='30.07%' overflowX='hidden' minW='27.07%' maxW='371px'>
        <Center
         bg='rgba(89, 143, 60, 0.1)'
         height='50px'
         borderRadius='12px'
         gap='16px'
         px='16px'
         w='full'
         maxW='370px'
        >
         <Text fontSize='sm' whiteSpace='nowrap'>
          Questões concluídas:
         </Text>

         <Box w='114px'>
          <Progress
           value={(totalAnswered / totalQuestions) * 100}
           height='10px'
           borderRadius='21px'
           colorScheme='green'
           bg='#D4D4D4'
          />
         </Box>

         <Center px='6px' h='32px' bg='white'>
          <Text color='#598F3C' fontSize='sm' fontWeight='bold'>
           {totalAnswered < 10 && '0'}
           {totalAnswered}/{totalQuestions < 10 && '0'}
           {totalQuestions}
          </Text>
         </Center>
        </Center>
       </Flex>
      </Flex>
     )}
     <Flex gap='5' overflowY='hidden' pos='absolute' right='32px' top='70px'>
      <DownloadResponsesButton
       surveyId={certificationData?.id_survey}
       companyId={selectedCompanyId}
       companyGroupId={companyGroupId}
       formName={formName}
       isLoadingResponses={isLoadingResponses}
       setLoadingResponses={setLoadingResponses}
      />

      <Button
       variant='secondary'
       width='260px'
       height='51px'
       isLoading={isLoadingEvidence}
       isDisabled={!hasEvidenceFiles}
       onClick={evidenceDownload}
      >
       Download das evidências
      </Button>

      {isResponsibleUser && (certificationStatus?.status || !certificationStatus) && (
       <SubmitAnswersButton
        certificationStatusId={certificationStatus?.id}
        mutate={mutate}
        isEnabled={!certificationStatus || certificationStatus?.status === 'não enviado'}
        selectedCertification={selectedCertificationId}
        companyId={selectedCompanyId}
        idCompanyGroup={companyGroupId}
        userId={user?.user?.id_user}
       />
      )}
     </Flex>
    </Flex>
   </Box>

   <Box mt='6'>
    {currentQuestions?.map((question, index) => (
     <QuestionCardSCertificationType
      key={`${question?.id_question}_${index}`}
      question={question}
      selectedCompanyId={selectedCompanyId}
      handleQuestionEvidence={handleQuestionEvidence}
      surveyId={surveyId}
      userId={user?.user?.id_user}
      questionnaireType='certification'
     />
    ))}
   </Box>

   <EditStatusModal
    isOpen={isEditStatusModalOpen}
    onClose={() => setIsEditStatusModalOpen(false)}
    currentStatus={certificationStatus?.status}
    certificateId={certificationStatus?.id}
    onStatusUpdate={() => mutate()}
    certificateStatusControlId={certificationStatus?.id}
    control={certificationStatus}
   />

   {currentQuestionId && (
    <AttachEvidenceModal
     isOpen={isEvidenceModalOpen}
     onClose={handleCloseCurrentEvidence}
     questionId={currentQuestionId}
     files={currentFiles}
    />
   )}
  </Box>
 );
}
